import { useState, useEffect } from "react";
import { Button, Card, Form, Input, message, Modal, Table, Popconfirm, Space, Upload, Typography } from "antd";
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Header from "../components/header/Header";
import { apiRequest } from "../utils/apiClient";
import { setSelectedBranch } from "../redux/userSlice";

const BranchesPage = () => {
  const { t } = useTranslation("pages");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedBranchId = useSelector((state) => state.user?.selectedBranchId ?? "");
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [form] = Form.useForm();
  const [bulkUploading, setBulkUploading] = useState(false);
  const [portModalOpen, setPortModalOpen] = useState(false);
  const [pendingCreateValues, setPendingCreateValues] = useState(null);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const data = await apiRequest("/api/branches", { method: "GET" });
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(err?.message || t("failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const createBranch = async (payload, portData) => {
    setSubmitting(true);
    try {
      const body = { ...payload };
      if (portData) {
        body.portDataFromBranchId = selectedBranchId || null;
      }
      const created = await apiRequest(
        "/api/branches",
        { method: "POST", body: JSON.stringify(body) },
        true
      );
      const ported = created?.ported;
      if (ported && (ported.products > 0 || ported.categories > 0)) {
        message.success(t("branchCreatedWithData", { products: ported.products, categories: ported.categories }));
      } else {
        message.success(t("branchCreated"));
      }
      form.resetFields();
      setEditingBranch(null);
      setPortModalOpen(false);
      setPendingCreateValues(null);
      fetchBranches();
      if (created?._id) dispatch(setSelectedBranch(created._id));
    } catch (err) {
      const msg = err?.data?.message || err?.data?.errors?.[0]?.msg || err?.message;
      message.error(msg || t("somethingWentWrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const onFinish = async (values) => {
    const payload = {
      name: values.name.trim(),
      location: values.location.trim(),
      address: (values.address || "").trim(),
    };

    const isEditing = !!editingBranch?._id;
    if (isEditing) {
      setSubmitting(true);
      try {
        await apiRequest(
          `/api/branches/${editingBranch._id}`,
          { method: "PUT", body: JSON.stringify(payload) },
          true
        );
        message.success(t("branchUpdated"));
        form.resetFields();
        setEditingBranch(null);
        fetchBranches();
      } catch (err) {
        const msg = err?.data?.message || err?.data?.errors?.[0]?.msg || err?.message;
        message.error(msg || t("somethingWentWrong"));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setPendingCreateValues(payload);
    setPortModalOpen(true);
  };

  const handlePortModalChoice = (portData) => {
    if (!pendingCreateValues) return;
    createBranch(pendingCreateValues, portData);
  };

  const handlePortModalCancel = () => {
    setPortModalOpen(false);
    setPendingCreateValues(null);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    form.setFieldsValue({
      name: branch.name || "",
      location: branch.location || "",
      address: branch.address || "",
    });
  };

  const handleDelete = async (branch) => {
    setSubmitting(true);
    try {
      await apiRequest(
        `/api/branches/${branch._id}`,
        {
          method: "DELETE",
        },
        true
      );
      message.success(t("branchDeleted"));

      if (editingBranch && editingBranch._id === branch._id) {
        setEditingBranch(null);
        form.resetFields();
      }

      fetchBranches();
    } catch (err) {
      const msg = err?.data?.message || err?.data?.errors?.[0]?.msg || err?.message;
      message.error(msg || t("somethingWentWrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: t("branchName"),
      dataIndex: "name",
      key: "name",
      render: (name) => name || "—",
    },
    {
      title: t("location"),
      dataIndex: "location",
      key: "location",
      render: (loc) => loc || "—",
    },
    {
      title: t("address"),
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      render: (addr) => addr || "—",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t("editBranch")}
          </Button>
          <Popconfirm
            title={t("delete")}
            description={t("deleteBranchConfirm")}
            okText={t("delete")}
            okButtonProps={{ danger: true }}
            cancelText={t("close")}
            onConfirm={() => handleDelete(record)}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const downloadTemplate = () => {
    const header = "name,location,address\n";
    const exampleRow = "Main Branch,Lahore,123 Main Road\n";
    const blob = new Blob([header + exampleRow], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "branches-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parseBranchesCsv = (text) => {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      return { branches: [], errors: [{ index: -1, message: "emptyFile" }] };
    }

    const headerParts = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const nameIndex = headerParts.indexOf("name");
    const locationIndex = headerParts.indexOf("location");
    const addressIndex = headerParts.indexOf("address");

    if (nameIndex === -1 || locationIndex === -1) {
      return { branches: [], errors: [{ index: -1, message: "missingRequiredColumns" }] };
    }

    const branchesFromCsv = [];
    const errors = [];

    for (let i = 1; i < lines.length; i += 1) {
      const row = lines[i];
      if (!row) continue;

      const parts = row.split(",").map((p) => p.trim());
      const name = parts[nameIndex] || "";
      const location = parts[locationIndex] || "";
      const address = addressIndex !== -1 ? parts[addressIndex] || "" : "";

      if (!name || !location) {
        errors.push({
          index: i,
          message: "nameLocationRequired",
        });
        continue;
      }

      branchesFromCsv.push({
        name,
        location,
        address,
      });
    }

    return { branches: branchesFromCsv, errors };
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;

    const isCsv =
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel" ||
      (file.name && file.name.toLowerCase().endsWith(".csv"));

    if (!isCsv) {
      message.error(t("branchesCsvInvalid"));
      return;
    }

    setBulkUploading(true);
    try {
      const text = await file.text();
      const { branches: parsedBranches, errors } = parseBranchesCsv(text);

      if (errors.some((e) => e.index === -1 && e.message === "emptyFile")) {
        message.error(t("branchesCsvEmptyFile"));
        return;
      }

      if (errors.some((e) => e.index === -1 && e.message === "missingRequiredColumns")) {
        message.error(t("branchesCsvRequiredColumns"));
        return;
      }

      if (!parsedBranches.length) {
        message.error(t("branchesBulkAllFailed"));
        return;
      }

      if (parsedBranches.length > 500) {
        message.error(t("branchesCsvHint"));
        return;
      }

      const result = await apiRequest(
        "/api/branches/bulk",
        {
          method: "POST",
          body: JSON.stringify({ branches: parsedBranches }),
        },
        true
      );

      const createdCount = result?.createdCount || 0;
      const failedCount = result?.failedCount || 0;

      message.success(
        t("branchesBulkCreated", {
          created: createdCount,
          failed: failedCount,
        })
      );

      fetchBranches();
    } catch (err) {
      const msg = err?.data?.message || err?.message;
      message.error(msg || t("somethingWentWrong"));
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="px-4 md:px-6 pb-20 max-w-4xl mx-auto">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="mb-4 -ml-2 text-gray-600 hover:text-gray-800"
        >
          {t("back")}
        </Button>
        <h1 className="text-2xl font-semibold mb-6 text-gray-800">
          {t("branches")}
        </h1>

        <Card title={editingBranch ? t("editBranch") : t("addBranch")} className="mb-8">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Form.Item
              label={t("branchName")}
              name="name"
              rules={[{ required: true, message: t("branchNameRequired") }]}
            >
              <Input placeholder={t("branchNamePlaceholder")} maxLength={100} />
            </Form.Item>
            <Form.Item
              label={t("location")}
              name="location"
              rules={[{ required: true, message: t("locationRequired") }]}
            >
              <Input placeholder={t("locationPlaceholder")} maxLength={100} />
            </Form.Item>
            <Form.Item label={t("address")} name="address">
              <Input.TextArea placeholder={t("addressPlaceholder")} rows={2} maxLength={300} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={editingBranch ? <EditOutlined /> : <PlusOutlined />}
              >
                {editingBranch ? t("updateBranch") : t("addBranch")}
              </Button>
            </Form.Item>
          </Form>
          <div className="mt-6 border-t pt-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <Typography.Text strong>{t("bulkBranches")}</Typography.Text>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
                className="p-0"
              >
                {t("downloadBranchesTemplate")}
              </Button>
            </div>
            <Typography.Paragraph type="secondary" className="mb-3">
              {t("branchesCsvHint")}
            </Typography.Paragraph>
            <Upload
              accept=".csv,text/csv"
              showUploadList={false}
              beforeUpload={(file) => {
                handleCsvUpload(file);
                return false;
              }}
            >
              <Button
                icon={<UploadOutlined />}
                loading={bulkUploading}
                disabled={bulkUploading || submitting}
              >
                {t("uploadBranchesCsv")}
              </Button>
            </Upload>
          </div>
        </Card>

        <Card title={t("branchList")}>
          <Table
            loading={loading}
            dataSource={branches}
            columns={columns}
            rowKey="_id"
            pagination={false}
            locale={{ emptyText: t("noBranches") }}
          />
        </Card>

        <Modal
          title={t("portDataToBranchTitle")}
          open={portModalOpen}
          onCancel={handlePortModalCancel}
          footer={[
            <Button key="empty" onClick={() => handlePortModalChoice(false)} loading={submitting} disabled={submitting}>
              {t("createEmptyBranch")}
            </Button>,
            <Button key="port" type="primary" onClick={() => handlePortModalChoice(true)} loading={submitting} disabled={submitting}>
              {t("portDataToBranchYes")}
            </Button>,
          ]}
        >
          <Typography.Paragraph>{t("portDataToBranchDescription")}</Typography.Paragraph>
        </Modal>
      </div>
    </>
  );
};

export default BranchesPage;
